import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import staffIdService from '../../services/staffIdService';

const IdCardDesignStudio = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateConfig, setTemplateConfig] = useState({
    templateName: '',
    templateDescription: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#10b981',
    fontFamily: 'Arial',
    layoutConfig: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewMode, setPreviewMode] = useState('front');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await staffIdService?.getAllTemplates();
      setTemplates(data || []);
      if (data?.length > 0) {
        const defaultTemplate = data?.find(t => t?.isDefault) || data?.[0];
        setSelectedTemplate(defaultTemplate?.id);
        setTemplateConfig({
          templateName: defaultTemplate?.templateName,
          templateDescription: defaultTemplate?.templateDescription,
          primaryColor: defaultTemplate?.primaryColor,
          secondaryColor: defaultTemplate?.secondaryColor,
          fontFamily: defaultTemplate?.fontFamily,
          layoutConfig: defaultTemplate?.layoutConfig
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates?.find(t => t?.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setTemplateConfig({
        templateName: template?.templateName,
        templateDescription: template?.templateDescription,
        primaryColor: template?.primaryColor,
        secondaryColor: template?.secondaryColor,
        fontFamily: template?.fontFamily,
        layoutConfig: template?.layoutConfig
      });
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (selectedTemplate) {
        await staffIdService?.updateTemplate(selectedTemplate, {
          templateName: templateConfig?.templateName,
          templateDescription: templateConfig?.templateDescription,
          primaryColor: templateConfig?.primaryColor,
          secondaryColor: templateConfig?.secondaryColor,
          fontFamily: templateConfig?.fontFamily,
          layoutConfig: templateConfig?.layoutConfig
        });
        setSuccess('Template updated successfully');
      } else {
        await staffIdService?.createTemplate({
          ...templateConfig,
          isActive: true,
          isDefault: false,
          createdBy: 'current_user'
        });
        setSuccess('Template created successfully');
      }

      await loadTemplates();
    } catch (err) {
      setError(err?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            ID Card Design Studio
          </h1>
          <p className="text-muted-foreground">
            Customize ID card templates with real-time preview and PNG government compliance
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="CheckCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Template Library Sidebar */}
          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} />
                Template Library
              </h3>

              <div className="space-y-2 mb-4">
                {templates?.map((template) => (
                  <div
                    key={template?.id}
                    onClick={() => handleTemplateSelect(template?.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTemplate === template?.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground">{template?.templateName}</p>
                      {template?.isDefault && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{template?.templateDescription}</p>
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateConfig({
                    templateName: 'New Template',
                    templateDescription: '',
                    primaryColor: '#3b82f6',
                    secondaryColor: '#10b981',
                    fontFamily: 'Arial',
                    layoutConfig: {}
                  });
                }}
              >
                <Icon name="Plus" size={14} className="mr-2" />
                New Template
              </Button>
            </div>
          </div>

          {/* Design Canvas */}
          <div className="lg:col-span-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Layout" size={20} />
                  Card Preview
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant={previewMode === 'front' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('front')}
                  >
                    Front
                  </Button>
                  <Button
                    variant={previewMode === 'back' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('back')}
                  >
                    Back
                  </Button>
                </div>
              </div>

              {/* Card Preview */}
              <div className="flex items-center justify-center py-12 bg-muted/30 rounded-lg">
                <div
                  className="relative w-80 h-52 rounded-lg shadow-xl overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${templateConfig?.primaryColor} 0%, ${templateConfig?.secondaryColor} 100%)`
                  }}
                >
                  {previewMode === 'front' ? (
                    <div className="p-6 text-white">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                          <Icon name="Building2" size={32} className="text-white" />
                        </div>
                        <div className="w-20 h-24 bg-white/20 rounded-lg flex items-center justify-center">
                          <Icon name="User" size={40} className="text-white" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-lg font-bold" style={{ fontFamily: templateConfig?.fontFamily }}>
                          John Doe
                        </p>
                        <p className="text-sm opacity-90">Senior Manager</p>
                        <p className="text-sm opacity-90">Human Resources</p>
                        <p className="text-sm font-semibold mt-2">ID: EMP-001</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 text-white flex flex-col items-center justify-center h-full">
                      <div className="w-32 h-32 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                        <Icon name="QrCode" size={64} className="text-white" />
                      </div>
                      <p className="text-xs text-center opacity-75">
                        Scan for employee verification
                      </p>
                      <div className="mt-4 w-full h-8 bg-white/20 rounded" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="Ruler" size={14} />
                  <span>CR80 Standard (85.6 × 53.98 mm)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Panel */}
          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Settings" size={20} />
                Properties
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Template Name
                  </label>
                  <Input
                    type="text"
                    value={templateConfig?.templateName}
                    onChange={(e) => setTemplateConfig(prev => ({ ...prev, templateName: e?.target?.value }))}
                    placeholder="Enter template name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <Input
                    type="text"
                    value={templateConfig?.templateDescription}
                    onChange={(e) => setTemplateConfig(prev => ({ ...prev, templateDescription: e?.target?.value }))}
                    placeholder="Template description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Primary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={templateConfig?.primaryColor}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, primaryColor: e?.target?.value }))}
                      className="w-12 h-10 rounded border border-border cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={templateConfig?.primaryColor}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, primaryColor: e?.target?.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Secondary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={templateConfig?.secondaryColor}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, secondaryColor: e?.target?.value }))}
                      className="w-12 h-10 rounded border border-border cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={templateConfig?.secondaryColor}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, secondaryColor: e?.target?.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Font Family
                  </label>
                  <Select
                    value={templateConfig?.fontFamily}
                    onChange={(e) => setTemplateConfig(prev => ({ ...prev, fontFamily: e?.target?.value }))}
                  >
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                  </Select>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleSaveTemplate}
                    disabled={loading || !templateConfig?.templateName}
                  >
                    {loading ? (
                      <>
                        <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Icon name="Save" size={16} className="mr-2" />
                        Save Template
                      </>
                    )}
                  </Button>
                </div>

                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-foreground mb-2">PNG Compliance</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-success">
                      <Icon name="CheckCircle" size={12} />
                      <span>Government standard format</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-success">
                      <Icon name="CheckCircle" size={12} />
                      <span>Security features enabled</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-success">
                      <Icon name="CheckCircle" size={12} />
                      <span>QR code encryption ready</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdCardDesignStudio;